import React, { useState, useRef, useEffect } from "react";
import { Space, Table, Button, Input, Modal, message } from "antd";
import {
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";

import IconGroup from "./IconGroup";
import CreateModalContent from "./CreateModalContent";
import { LoadingIcon } from "../../../components";
import {
  getOrgs,
  putOrg,
  postOrg,
  putOrgStatus,
  deleteOrg,
} from "../../../utils/api";

const OrgTable = ({ cropData }) => {
  const [data, setData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [, setSearchText] = useState("");
  const [, setSearchedColumn] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const searchInput = useRef(null);

  const handleCreateCancel = () => {
    setCreateModalOpen(false);
  };

  // START - API Functions
  const fetchOrgs = async () => {
    try {
      setDataLoading(true);
      const res = await getOrgs();
      const newData = res.data.data.map((org) => {
        return {
          ...org,
          key: org.id,
        };
      });
      setData(newData);
    } catch (err) {
      message.error("Error fetching Organizations!");
    } finally {
      setDataLoading(false);
    }
  };

  const updateOrg = async (data) => {
    const id = data.id;

    try {
      await putOrg(id, data);
      await fetchOrgs();
    } catch (err) {
      message.error("Error fetching Organizations!");
    }
  };

  const hideOrg = async (id, data) => {
    try {
      await putOrgStatus(id, data);
      await fetchOrgs();
    } catch (err) {
      message.error("Error fetching Organizations!");
    }
  };

  const createOrg = async (data) => {
    try {
      await postOrg(data);
      await fetchOrgs();
    } catch (err) {
      message.error("Error fetching Organizations!");
    }
  };

  const removeOrg = async (id) => {
    try {
      await deleteOrg(id);
      await fetchOrgs();
    } catch (err) {
      message.error("Error fetching Organizations!");
    }
  };
  // END - API Functions

  useEffect(() => {
    fetchOrgs();
  }, []);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Logo",
      dataIndex: "logo",
      key: "logo",
      render: (url) =>
        url ? (
          <img
            src={url}
            alt="logo"
            style={{
              width: "30px",
            }}
          />
        ) : null,
    },
    {
      title: "Crops",
      dataIndex: "crops",
      key: "crops",
      render: (crops) =>
        Array.isArray(crops) ? crops.map((crop) => crop?.display_name).join(", ") : "-",
    },
    {
      title: "Max Site",
      dataIndex: "maximum_site_allowed",
      key: "maxSite",
    },
    {
      title: "Max User",
      dataIndex: "maximum_user_allowed",
      key: "maxUser",
    },
    {
      title: "Max Crop",
      dataIndex: "maximum_crop_allowed",
      key: "maxCrop",
    },
    {
      title: "Max Plot",
      dataIndex: "maximum_plot_allowed",
      key: "maxPlot",
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "active",
      render: (value) =>
        !!value ? (
          <CheckCircleOutlined className="text-successGreen" />
        ) : (
          <CloseCircleOutlined className="text-dangerRed" />
        ),
    },
    {
      title: "Breeder",
      dataIndex: "is_breeder",
      key: "breeder",
      render: (value) =>
        !!value ? (
          <CheckCircleOutlined className="text-successGreen" />
        ) : (
          <CloseCircleOutlined className="text-dangerRed" />
        ),
    },
    {
      title: "Grower",
      dataIndex: "is_grower",
      key: "grower",
      render: (value) =>
        !!value ? (
          <CheckCircleOutlined className="text-successGreen" />
        ) : (
          <CloseCircleOutlined className="text-dangerRed" />
        ),
    },
    {
      title: "Pollination",
      dataIndex: "is_pollination",
      key: "pollination",
      render: (value) =>
        !!value ? (
          <CheckCircleOutlined className="text-successGreen" />
        ) : (
          <CloseCircleOutlined className="text-dangerRed" />
        ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <IconGroup
          ele={record}
          updateCallback={updateOrg}
          hideCallback={hideOrg}
          deleteCallback={removeOrg}
          cropData={cropData}
        />
      ),
    },
  ];

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({
                closeDropdown: true,
              });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#39f" : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const finalColumns = columns.map((vals) => {
    const obj =
      vals.key === "actions"
        ? { ...vals }
        : { ...vals, ...getColumnSearchProps(vals.key) };
    return obj;
  });

  return (
    <div>
      {dataLoading ? (
        <LoadingIcon classname='absolute h-full top-[40vh]' height="200px" />
      ) : (
        <>
          <div className="w-full pb-10 h-fit flex justify-end sm:justify-start items-center">
            <Button type="primary" onClick={() => setCreateModalOpen(true)}>
              Create Organization
            </Button>
          </div>
          <Table
            dataSource={data}
            columns={finalColumns}
            pagination={{
              hideOnSinglePage: true,
              position: ["bottomCenter"],
            }}
          />
        </>
      )}
      <Modal
        maskClosable={false}
        title={
          <div className="mb-5 font-IBMPlexSansBold text-2xl">
            Create Organization
          </div>
        }
        open={createModalOpen}
        onCancel={handleCreateCancel}
        footer={null}
      >
        <CreateModalContent
          setOpen={setCreateModalOpen}
          createCallback={createOrg}
          cropData={cropData}
        />
      </Modal>
    </div>
  );
};

export default OrgTable;
